import { DateTimeInfo } from "../../DateTimeInfo";
import { Nullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";

export enum DailyFrequencyTypeEnum
{
    once = "once",
    recurring = "recurring",
}

export enum DayNumberEnum
{
    first = "first",
    second = "second",
    third = "third",
    fourth = "fourth",
    last = "last",
}

export enum DayTypeEnum
{
    monday = "monday",
    tuesday = "tuesday",
    wednesday = "wednesday",
    thursday = "thursday",
    friday = "friday",
    saturday = "saturday",
    sunday = "sunday",
    day = "day",
    weekDay = "weekDay",
    weekendDay = "weekendDay",
}

export enum DurationEndTypeEnum
{
    none = "none",
    finite = "finite",
}

export enum FrequencyEnum
{
    oneTime = "oneTime",
    daily = "daily",
    weekly = "weekly",
    monthly = "monthly",
}

export enum MonthlyFrequencyTypeEnum
{
    dayByNumber = "dayByNumber",
    dayByDay = "dayByDay",
}

export enum TimePeriodEnum
{
    hours = "hours",
    minutes = "minutes",
    seconds = "seconds",
}

export interface IScheduleObject
{
    timeZoneId: string;
    frequency: FrequencyEnum;
    oneTimeDateTime: Nullable<string>;
    frequencyDailyEveryX: Nullable<number>;
    frequencyWeeklyEveryX: Nullable<number>;
    frequencyWeeklyOnMonday: Nullable<boolean>;
    frequencyWeeklyOnTuesday: Nullable<boolean>;
    frequencyWeeklyOnWednesday: Nullable<boolean>;
    frequencyWeeklyOnThursday: Nullable<boolean>;
    frequencyWeeklyOnFriday: Nullable<boolean>;
    frequencyWeeklyOnSaturday: Nullable<boolean>;
    frequencyWeeklyOnSunday: Nullable<boolean>;
    frequencyMonthlyFrequencyType: Nullable<MonthlyFrequencyTypeEnum>;
    frequencyMonthlyDayByNumberDayX: Nullable<number>;
    frequencyMonthlyDayByNumberEveryX: Nullable<number>;
    frequencyMonthlyDayByDayDayNumber: Nullable<DayNumberEnum>;
    frequencyMonthlyDayByDayDayType: Nullable<DayTypeEnum>;
    frequencyMonthlyDayByDayEveryX: Nullable<number>;
    dailyFrequencyType: Nullable<DailyFrequencyTypeEnum>;
    dailyFrequencyOnceTime: string;
    dailyFrequencyEveryX: Nullable<number>;
    dailyFrequencyEveryTimePeriod: TimePeriodEnum;
    dailyFrequencyEveryStartTime: string;
    dailyFrequencyEveryEndTime: string;
    durationStartDate: string;
    durationEndType: DurationEndTypeEnum;
    durationEndDate: Nullable<string>;
};

export class ScheduleStatic
{
    public static getDescription(schedule: IScheduleObject)
    {
        let
            description = (schedule.timeZoneId ?? "UTC") + ": ",
            oneTimeDateTime: DateTimeInfo,
            durationStartDate: DateTimeInfo,
            durationEndDate: DateTimeInfo | undefined = undefined;

        switch (schedule.frequency)
        {
            case "oneTime":
                oneTimeDateTime = DateTimeInfo.fromDateString(schedule.oneTimeDateTime!);
                description +=
                    `Occurs on ${oneTimeDateTime.toString("dd/MM/yyyy")} at ${oneTimeDateTime.toString("HH:mm:ss")}.`;
                break;
            case "daily":
                description +=
                    `Occurs every${schedule.frequencyDailyEveryX! > 1 ? " " + schedule.frequencyDailyEveryX : StringHelper.empty} day${schedule.frequencyDailyEveryX! > 1 ? "s" : StringHelper.empty}`;
                break;
            case "weekly":
                description +=
                    `Occurs every${schedule.frequencyWeeklyEveryX! > 1 ? " " + schedule.frequencyWeeklyEveryX : StringHelper.empty} week${schedule.frequencyWeeklyEveryX! > 1 ? "s" : StringHelper.empty}${this.getDayList(schedule)}`;
                break;
            case "monthly":
                switch (schedule.frequencyMonthlyFrequencyType)
                {
                    case "dayByNumber":
                        description +=
                            `Occurs every${schedule.frequencyMonthlyDayByNumberEveryX! > 1 ? " " + schedule.frequencyMonthlyDayByNumberEveryX : StringHelper.empty} month${schedule.frequencyMonthlyDayByNumberEveryX! > 1 ? "s" : StringHelper.empty} on day ${schedule.frequencyMonthlyDayByNumberDayX} of that month`;
                        break;
                    case "dayByDay":
                        description +=
                            `Occurs every ${this.getDayNumberDescription(schedule.frequencyMonthlyDayByDayDayNumber!)} ${this.getDayTypeDescription(schedule.frequencyMonthlyDayByDayDayType!)} of every${schedule.frequencyMonthlyDayByDayEveryX! > 1 ? " " + schedule.frequencyMonthlyDayByDayEveryX : StringHelper.empty} month${schedule.frequencyMonthlyDayByDayEveryX! > 1 ? "s" : StringHelper.empty}`;
                        break;
                }
                break;
        }

        switch (schedule.frequency)
        {
            case "oneTime":
                break;
            default:
                switch (schedule.dailyFrequencyType)
                {
                    case "once":
                        description +=
                            ` at ${schedule.dailyFrequencyOnceTime}.`;
                        break;
                    case "recurring":
                        description +=
                            ` every${schedule.dailyFrequencyEveryX! > 1 ? " " + schedule.dailyFrequencyEveryX : StringHelper.empty} ${this.getTimePeriodDescription(schedule.dailyFrequencyEveryTimePeriod)}${schedule.dailyFrequencyEveryX! > 1 ? "s" : StringHelper.empty} between ${schedule.dailyFrequencyEveryStartTime.toString()} and ${schedule.dailyFrequencyEveryEndTime.toString()}.`;
                        break;
                }
                break;
        }

        switch (schedule.frequency)
        {
            case "oneTime":
                break;
            default:
                durationStartDate = DateTimeInfo.fromDateString(schedule.durationStartDate);

                if (schedule.durationEndDate)
                    durationEndDate = DateTimeInfo.fromDateString(schedule.durationEndDate);

                description +=
                    ` Schedule will be used ${schedule.durationEndType === "none" ? "from" : "between"} ${durationStartDate.toString("dd/MM/yyyy")}${schedule.durationEndType === "finite" ? " and " + (durationEndDate ? durationEndDate.toString("dd/MM/yyyy") : StringHelper.empty) : StringHelper.empty}.`;
                break;
        }

        return description;
    }

    private static getDayList(schedule: IScheduleObject)
    {
        const
            days = [];

        if (schedule.frequencyWeeklyOnMonday)
            days.push("Monday");

        if (schedule.frequencyWeeklyOnTuesday)
            days.push("Tuesday");

        if (schedule.frequencyWeeklyOnWednesday)
            days.push("Wednesday");

        if (schedule.frequencyWeeklyOnThursday)
            days.push("Thursday");

        if (schedule.frequencyWeeklyOnFriday)
            days.push("Friday");

        if (schedule.frequencyWeeklyOnSaturday)
            days.push("Saturday");

        if (schedule.frequencyWeeklyOnSunday)
            days.push("Sunday");

        if (days.length > 0)
            return StringHelper.format(" on {0}", days.join(", "));

        return StringHelper.empty;
    }
    private static getDayNumberDescription(dayNumber: DayNumberEnum)
    {
        switch (dayNumber)
        {
            case "first":
                return "first";
            case "second":
                return "second";
            case "third":
                return "third";
            case "fourth":
                return "fourth";
            case "last":
                return "last";
            default:
                console.error("Not implemented.");
                break;
        }
    }
    private static getDayTypeDescription(dayType: DayTypeEnum)
    {
        switch (dayType)
        {
            case "monday":
                return "monday";
            case "tuesday":
                return "tuesday";
            case "wednesday":
                return "wednesday";
            case "thursday":
                return "thursday";
            case "friday":
                return "friday";
            case "saturday":
                return "saturday";
            case "sunday":
                return "sunday";
            case "day":
                return "day";
            case "weekDay":
                return "week day";
            case "weekendDay":
                return "weekend day";
            default:
                console.error("Not implemented.");
                break;
        }
    }
    private static getTimePeriodDescription(timePeriod: TimePeriodEnum)
    {
        switch (timePeriod)
        {
            case "hours":
                return "hour";
            case "minutes":
                return "minute";
            case "seconds":
                return "second";
            default:
                console.error("Not implemented.");
                break;
        }
    }
}